"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useKonamiCode } from "@/hooks/useKonamiCode";
import { createFullscreen, type FullscreenMode } from "./fullscreen";
import { loadLanguage, saveLanguage, uiText, type GameLanguage, type UiTextId } from "./language";
import { PAUSE_3D } from "@/components/3D/Utils/postFxTuning";
import { bootChirp, audioContext, isMuted, setMuted, subscribeMuted, suspendAudio, unlockAudio } from "./sound";
type GameModule = typeof import("./game");
type Phase = "idle" | "boot" | "error" | "game";
const BOOT_LINES: UiTextId[] = ["ui.cheatCodeAccepted", "ui.accessingMassifDeepSpaceNetwork", "ui.640kMemoryOk", "ui.loadingScrumExe"];

// The game chunk (Phaser + content) is only requested when the game opens.
let loading: Promise<GameModule> | null = null;
const loadGame = () => loading ??= import("./game").catch(e => {
  loading = null; // allow RETRY
  throw e;
});
export default function ScrumNebulaLauncher(t0) {
  const $ = _c(53);
  const {
    siteLanguage
  } = t0;
  const [language, setLanguage] = useState(siteLanguage === "de" ? "de" : "en");
  const currentLanguage = useRef(language);
  const session = useRef(null);
  let t1;
  if ($[0] !== language) {
    t1 = text => uiText(language, text);
    $[0] = language;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const [phase, setPhase] = useState("idle");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [lines, setLines] = useState(t2);
  const [artReady, setArtReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fullscreenMode, setFullscreenMode] = useState("off");
  const [fullscreenSupported, setFullscreenSupported] = useState(false);
  const fullscreen = useRef(null);
  const overlay = useRef(null);
  const host = useRef(null);
  const game = useRef(null);
  const stopBoot = useRef(null);
  const active = phase !== "idle";
  const muted = useSyncExternalStore(subscribeMuted, isMuted, _temp);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      stopBoot.current?.();
      stopBoot.current = null;
      suspendAudio();
      fullscreen.current?.exit();
      setFullscreenMode("off");
      setPhase("idle");
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const close = t3;
  let t4;
  if ($[4] !== siteLanguage) {
    t4 = () => {
      if (overlay.current) {
        return;
      }
      const ctx = unlockAudio();
      if (ctx) {
        stopBoot.current = bootChirp(ctx);
      }
      setLines([]);
      setArtReady(false);
      setProgress(0);
      currentLanguage.current = loadLanguage(siteLanguage);
      setLanguage(currentLanguage.current);
      setPhase(_temp2);
    };
    $[4] = siteLanguage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const open = t4;
  useKonamiCode(open);
  let t5;
  let t6;
  if ($[6] !== active) {
    t5 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const control = createFullscreen(overlay.current, setFullscreenMode);
      fullscreen.current = control;
      setFullscreenSupported(control.supported);
      return () => {
        control.dispose();
        fullscreen.current = null;
      };
    };
    t6 = [active];
    $[6] = active;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== open) {
    t7 = () => {
      const query = new URLSearchParams(window.location.search);
      if (query.has("konami") || process.env.NODE_ENV === "development" && (query.has("scrum-preview") || query.has("scrum-room"))) {
        open();
      }
    };
    t8 = [open];
    $[9] = open;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== language || $[13] !== phase) {
    t9 = () => {
      if (phase !== "boot") {
        return;
      }
      let alive = true;
      const visibility = _temp5;
      document.addEventListener("visibilitychange", visibility);
      if (document.hidden) {
        visibility();
      }
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const step = reduced ? 150 : 600;
      const timers = BOOT_LINES.map((_, i) => window.setTimeout(() => setLines(BOOT_LINES.slice(0, i + 1).map(line => uiText(language, line))), i * step));
      Promise.all([loadGame(), new Promise(r => window.setTimeout(r, BOOT_LINES.length * step))]).then(t11 => {
        const [mod] = t11;
        if (!alive) {
          return;
        }
        game.current = mod;
        setPhase("game");
      }).catch(() => {
        if (alive) {
          suspendAudio();
          setPhase("error");
        }
      });
      return () => {
        alive = false;
        timers.forEach(clearTimeout);
        document.removeEventListener("visibilitychange", visibility);
        stopBoot.current?.();
        stopBoot.current = null;
      };
    };
    t10 = [phase, language];
    $[12] = language;
    $[13] = phase;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== phase) {
    t11 = () => {
      if (phase !== "game" || !host.current || !game.current) {
        return;
      }
      host.current.focus();
      const running = game.current.startGame(host.current, {
        language: currentLanguage.current,
        onExit: close,
        onProgress: setProgress,
        onReady: () => setArtReady(true),
        onError: () => setPhase("error")
      });
      session.current = running;
      return () => {
        session.current = null;
        running.dispose();
      };
    };
    t12 = [phase, close];
    $[16] = phase;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[19] !== active) {
    t13 = () => {
      if (!active || !overlay.current) {
        return;
      }
      const self = overlay.current;
      const prevFocus = document.activeElement as HTMLElement | null;
      const siblings = [...document.body.children].filter(el => el instanceof HTMLElement && el !== self && !el.inert);
      siblings.forEach(_temp6);
      const root = document.documentElement;
      const overflow = root.style.overflow;
      root.style.overflow = "hidden";
      PAUSE_3D.value = true;
      const onKey = e => {
        if (e.key !== "Escape") {
          return;
        }
        e.stopPropagation();
        if (fullscreen.current && fullscreen.current.mode !== "off") {
          e.preventDefault();
          fullscreen.current?.exit();
          return;
        }
        close();
      };
      window.addEventListener("keydown", onKey, true);
      return () => {
        siblings.forEach(_temp7);
        if (root.style.overflow === "hidden") {
          root.style.overflow = overflow;
        }
        PAUSE_3D.value = false;
        window.removeEventListener("keydown", onKey, true);
        prevFocus?.focus();
      };
    };
    t14 = [active, close];
    $[19] = active;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  useEffect(t13, t14);
  if (!active) {
    return null;
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      paddingTop: "env(safe-area-inset-top)",
      paddingRight: "env(safe-area-inset-right)",
      paddingBottom: "env(safe-area-inset-bottom)",
      paddingLeft: "env(safe-area-inset-left)"
    };
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== lines || $[24] !== phase || $[25] !== t) {
    t16 = phase === "game" ? <div ref={host} tabIndex={-1} className="flex min-h-0 flex-1 items-center justify-center outline-none" /> : <pre className="overflow-auto whitespace-pre-wrap break-words p-4 text-xs leading-loose sm:p-6 sm:text-sm">{lines.join("\n")}{phase === "error" && `\n\n${t("ui.diskReadError")}\n${t("ui.abortRetryFail")}`}</pre>;
    $[23] = lines;
    $[24] = phase;
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== artReady || $[28] !== phase || $[29] !== progress || $[30] !== t) {
    t17 = phase === "game" && !artReady && <div className="absolute inset-0 flex items-center justify-center bg-black"><p role="status" className="px-4 text-center text-sm">{t("ui.loadingSectorArt")} {Math.round(progress * 100)}%</p></div>;
    $[27] = artReady;
    $[28] = phase;
    $[29] = progress;
    $[30] = t;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== phase || $[33] !== t) {
    t18 = phase === "error" && <div className="flex gap-4 px-6"><button type="button" onClick={() => {
        const ctx_1 = unlockAudio();
        if (ctx_1) {
          stopBoot.current = bootChirp(ctx_1);
        }
        setLines([]);
        setArtReady(false);
        setProgress(0);
        setPhase("boot");
      }} className="underline">{t("ui.retry")}</button><button type="button" onClick={close} className="underline">{t("ui.abort")}</button></div>;
    $[32] = phase;
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== fullscreenMode) {
    t19 = fullscreenMode !== "off" ? {
      top: "max(0.75rem, env(safe-area-inset-top))",
      right: "max(0.75rem, env(safe-area-inset-right))"
    } : undefined;
    $[35] = fullscreenMode;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = fullscreenMode !== "off" ? "absolute z-10" : "relative z-10 order-first grid shrink-0 grid-cols-2 gap-2 p-3 text-xs uppercase tracking-wide sm:flex sm:items-center sm:justify-end sm:gap-4 sm:px-4";
  let t21;
  if ($[37] !== fullscreenMode || $[38] !== fullscreenSupported || $[39] !== language || $[40] !== muted || $[41] !== t) {
    t21 = fullscreenMode !== "off" ? <button type="button" aria-label={t(fullscreenMode === "native" ? "ui.exitFullscreen" : "ui.showControls")} title={t(fullscreenMode === "native" ? "ui.exitFullscreen" : "ui.showControls")} onClick={() => {
      fullscreen.current?.exit();
    }} className="flex size-11 items-center justify-center bg-black/70 text-white hover:bg-black"><svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 9h5V4m11 5h-5V4M4 15h5v5m11-5h-5v5" /></svg></button> : <><label className="flex min-h-11 items-center justify-center gap-2 bg-white/5 px-3 sm:bg-transparent"><span className="sr-only sm:not-sr-only">{t("ui.language")}</span><select aria-label={t("ui.language")} value={language} onKeyDown={_temp11} onChange={event_3 => {
          const next = event_3.target.value === "de" ? "de" : "en";
          saveLanguage(next);
          setLines([]);
          currentLanguage.current = next;
          setLanguage(next);
          session.current?.setLanguage(next);
        }} className="min-h-11 cursor-pointer bg-transparent text-base hover:text-white sm:text-xs"><option value="en" lang="en">English</option><option value="de" lang="de">Deutsch</option></select></label><button type="button" aria-pressed={muted} onClick={() => setMuted(!muted)} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t(muted ? "ui.soundOff" : "ui.soundOn")}<span className="hidden sm:pointer-fine:inline"> [M]</span></button><button type="button" onClick={() => {
        fullscreen.current?.enter();
      }} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t(fullscreenSupported ? "ui.fullscreen" : "ui.hideControls")}</button><button type="button" onClick={close} className="min-h-11 bg-white/5 px-3 hover:text-white sm:bg-transparent">{t("ui.exit")}<span className="hidden sm:pointer-fine:inline"> [Esc]</span></button></>;
    $[37] = fullscreenMode;
    $[38] = fullscreenSupported;
    $[39] = language;
    $[40] = muted;
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t19 || $[44] !== t20 || $[45] !== t21) {
    t22 = <div onMouseDown={_temp8} onTouchStart={_temp9} onKeyDown={_temp10} style={t19} className={t20}>{t21}</div>;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== language || $[48] !== t16 || $[49] !== t17 || $[50] !== t18 || $[51] !== t22) {
    t23 = <div ref={overlay} role="dialog" aria-modal="true" aria-label="The Secret of the SCRUM Nebula" lang={language} style={t15} className="fixed inset-x-0 top-0 z-[10000] flex h-dvh flex-col bg-black font-secondary text-[#c0c0c0]">{t16}{t17}{t18}{t22}</div>;
    $[47] = language;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  return t23;
}
function _temp11(event_2) {
  return event_2.stopPropagation();
}
function _temp10(event_1) {
  if (event_1.key === " " || event_1.key === "Enter") {
    event_1.stopPropagation();
  }
}
function _temp9(event_0) {
  return event_0.stopPropagation();
}
function _temp8(event) {
  return event.stopPropagation();
}
function _temp7(el_1) {
  return el_1.inert = false;
}
function _temp6(el_0) {
  return el_0.inert = true;
}
function _temp5() {
  const ctx_0 = audioContext();
  if (!ctx_0) {
    return;
  }
  if (document.hidden) {
    ctx_0.suspend().catch(_temp3);
  } else {
    ctx_0.resume().catch(_temp4);
  }
}
function _temp4() {}
function _temp3() {}
function _temp2(p) {
  return p === "idle" ? "boot" : p;
}
function _temp() {
  return false;
}